import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { LOB_ENUM } from "../../../routes/intel/ProductOnboarding/utils";
import Composer from "./Composer";
import DottedBackground from "./DottedBackground";
import useHermesAnalytics from "@containers/Hermes/hooks/useHermesAnalytics";

type LandingViewProps = {
  isStreaming: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  onOpenHistory: () => void;
};

const PERSONA_WISE_SUGGESTIONS: Record<string, string[]> = {
  [LOB_ENUM.IMPORT]: ["Top 5 exporters of sweet corn from Mexico by shipment value", "Average monthly trade price of fresh apples from Chile", "New solar panel suppliers to US since 2025", "Top 15 suppliers of lithium-ion batteries to the United States"],
  [LOB_ENUM.EXPORT]: ["US buyers regularly importing leather footwear from Brazil", "Top 10 buyers of Vietnamese footwear in the United States", "Major buyers of Indonesian palm oil worldwide by value", "Largest importers of Indian pharmaceuticals globally by volume"],
  [LOB_ENUM.EXPORT_AND_IMPORT]: ["Average export price of Mexican avocados to the United States", "Top 5 suppliers and buyers of steel coils in Indonesia", "Price trend of fresh apples shipped from Chile to Vietnam", "Average price of walnuts exported from the United States to India"],
  [LOB_ENUM.BUSINESS_DEVELOPMENT]: ["Monthly shipment frequency of seafood on the Ecuador–US trade lane", "Largest shipping lanes for electronics from China to South America", "Shipment count of electronics on the China–United States trade lane, last 12 months", "Major trade routes for automotive parts from Mexico to the United States"],
  [LOB_ENUM.OTHER]: ["India's pharmaceutical export trends over the last five years", "Top traded commodities between China and the United States", "Countries which increased their imports of electric vehicles recently", "Global trade value of crude oil by exporting country"],
};

// const TARIFF_SUGGESTIONS: string[] = ["What tariffs are applicable on semiconductors imported in US from China?"];

// Vertical gaps from the Zeplin frame (758px tall), shrunk on shorter windows so
// the landing fits without scrolling. The fixed parts (heading, composer, cards)
// plus the bottom padding take 454px of the space below the 48px app header.
const gap = (share: number, min: number, max: number) => `clamp(${min}px, calc((100vh - 48px - 454px) * ${share}), ${max}px)`;

export default function LandingView({ isStreaming, onSend, onStop, onOpenHistory }: LandingViewProps) {
  const fullName = useSelector((state: any) => state?.userDetails?.userData?.fullName) || "there";
  const lineOfBusiness = useSelector((state: any) => state?.userDetails?.userData?.line_of_business);
  const suggestions = PERSONA_WISE_SUGGESTIONS[lineOfBusiness] || PERSONA_WISE_SUGGESTIONS[LOB_ENUM.OTHER];
  // const tariffSuggestion = useMemo(() => TARIFF_SUGGESTIONS[Math.floor(Math.random() * TARIFF_SUGGESTIONS.length)], []);
  // const finalSuggestions = useMemo(() => [...suggestions, tariffSuggestion], [suggestions, tariffSuggestion]);
  const finalSuggestions = useMemo(() => [...suggestions], [suggestions]);
  const analytics = useHermesAnalytics();

  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  useEffect(() => {
    setPlaceholderIndex(0);
    const id = setInterval(() => {
      setPlaceholderIndex((i) => (i + 1) % suggestions.length);
    }, 5000);
    return () => clearInterval(id);
  }, [suggestions]);

  return (
    <div className="tw-relative tw-h-full tw-overflow-y-auto">
      <DottedBackground />

      <button onClick={onOpenHistory} className="tw-absolute tw-z-10 tw-top-3 tw-right-4 tw-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-3 tw-rounded-lg tw-border-none tw-bg-transparent hover:tw-bg-gray-50 tw-cursor-pointer tw-text-sm tw-font-medium tw-leading-5 tw-tracking-[-0.07px] tw-text-gray-700 hover:tw-text-gray-900 tw-transition-colors">
        <span className="material-symbols-outlined !tw-text-[18px]">history</span>
        Chat history
      </button>

      <div className="tw-relative tw-mx-auto tw-w-full tw-max-w-[722px] tw-px-4 tw-box-border tw-pb-4" style={{ paddingTop: gap(0.308, 16, 77) }}>
        <div className="tw-text-center tw-text-xl tw-leading-7 tw-tracking-[-0.1px]">
          <h1 className="tw-m-0 tw-text-xl tw-leading-7 tw-font-semibold tw-text-gray-900 tw-tracking-[-0.1px] text-capitalize">Hi {fullName}</h1>
          <p className="tw-m-0 tw-font-normal tw-text-gray-500">How can I help you today?</p>
        </div>

        <div style={{ marginTop: gap(0.216, 24, 54) }}>
          <Composer isStreaming={isStreaming} onSend={onSend} onStop={onStop} placeholder={finalSuggestions[placeholderIndex]} />
        </div>

        <div style={{ marginTop: gap(0.476, 32, 119) }}>
          <h2 className="tw-m-0 tw-text-base tw-leading-6 tw-font-medium tw-text-gray-900 tw-tracking-[-0.08px]">Example queries</h2>
          <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-x-[15px] tw-gap-y-4">
            {finalSuggestions.map((text, i) => (
              <button
                key={text}
                onClick={() => {
                  analytics.suggestionClicked({ suggestionType: "landing", text, position: i });
                  onSend(text);
                }}
                className="tw-flex tw-flex-col tw-items-start tw-justify-between tw-gap-3 tw-min-h-[121px] tw-box-border tw-p-[13px] tw-text-left tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-rounded-xl tw-cursor-pointer hover:tw-border-primary-purple-300 tw-transition-colors tw-duration-150">
                <span className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8">
                  <span className="material-symbols-outlined !tw-text-[24px] tw-text-[#1c1b1f]" aria-hidden="true">
                    arrow_outward
                  </span>
                </span>
                <span className="tw-text-sm tw-leading-5 tw-tracking-[-0.07px] tw-text-gray-900">{text}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
